import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import {IDatasetTableCommonProps} from "components/dashboard/dataset-table/utils/interfaces";
import {
    calculateColSpan,
    CombinedColumnRows,
    findHighlightRange,
    WIDTH_LOOKUP_TABLE,
    StyledRows
} from "components/dashboard/dataset-table/utils/utils";
import {TitleOverrideAndDescriptionTemplate} from "components/templates/DataTableCellTemplates";
import {DatasetDefaultConfig} from "models/dataset-models";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Row} from "primereact/row";
import React from "react";
import {Tooltip} from "react-tooltip";
import {ChronoUnitLegacy, dateToReadableString} from "utils/time-utils";


const DatasetTableGrouped = ({config, data, datasetsArray, totals, setIsDialogOpen}: IDatasetTableCommonProps) => {
    const createHeaderGroup = (datasets: any[], level = 0): any[] => {
        let rows: JSX.Element[] = [];
        let rowElements: JSX.Element[] = [];

        datasets.forEach((dataset, index) => {
            if (dataset.header) {
                let colSpan = calculateColSpan(dataset.children);
                rowElements.push(
                    <Column
                        key={`${dataset.header}-${level}`}
                        align="center"
                        colSpan={colSpan}
                        header={dataset.header}
                    />
                );
            }

            if (dataset.children) {
                rows.push(...createHeaderGroup(dataset.children, level + 1));
            } else {
                rowElements.push(
                    <Column
                        key={dataset.identifier}
                        align="center"
                        field={dataset.identifier}
                        header={<div className="flex gap-1"> {TitleOverrideAndDescriptionTemplate({
                            description: dataset.description,
                            title: dataset.title,
                            overriddenTitle: dataset.overriddenTitle,
                            unit: dataset.unit,
                            identifier: dataset.identifier
                        }, 2)}
                            <div className={"non-printable"}>
                                <ChartInDialogButton identifier={dataset.identifier} setIsDialogOpen={setIsDialogOpen}/>
                            </div>
                        </div>}
                    />
                );
            }

            if (index === datasets.length - 1 && rowElements.length > 0) {
                if (level === 0 && !config?.hideDateColumn) {
                    rowElements.unshift(<Column key="date" header="Date" rowSpan={rows.length + 1}/>);
                }
                rows.unshift(<Row key={`row-${level}`}>{rowElements}</Row>);
            }
        });

        return rows;
    };

    const rows = createHeaderGroup(datasetsArray);

    let groupedRows: {[key: string]: React.ReactElement} = {};
    for (let i = 0; i < rows.length; i++) {
        const key = rows[i].key;
        if (groupedRows[key]) {
            groupedRows[key] = CombinedColumnRows(groupedRows[key], rows[i]);
        } else {
            groupedRows[key] = rows[i];
        }
    }

    const CombinedAndStyledRows = StyledRows(Object.values(groupedRows));

    const headerGroup = <ColumnGroup>{CombinedAndStyledRows}</ColumnGroup>;

    const extractDatasets = (datasets: any[]): any[] => {
        let extracted: DatasetDefaultConfig[] = [];
        for (let dataset of datasets) {
            if (dataset.children) {
                extracted = [...extracted, ...extractDatasets(dataset.children)];
            } else {
                extracted.push(dataset);
            }
        }
        return extracted;
    };

    const extractedDatasets = extractDatasets(datasetsArray);

    const columnValueBodyTemplate = (rowData: any, column: any) => {
        const value = rowData[column.field];
        const correspondingDataset = extractedDatasets.find(d => d.identifier === column.field);
        if (correspondingDataset && typeof value === "number") {
            const highlightRange = findHighlightRange(value, correspondingDataset.highlightRanges || []);
            const style = highlightRange ? {
                color: highlightRange.color,
                backgroundColor: highlightRange.bgColor,
                padding: "0.5rem"
            } : {padding: "0.5rem"};
            return <div style={style}>
                {value.toFixed(correspondingDataset.decimalPlaces)}
            </div>;
        } else {
            return <div style={{padding: "0.5rem"}}>
                -
            </div>;
        }
    };

    const createColumns = (datasets: any[]): any[] => {
        return datasets.flatMap((dataset) => {
            if (dataset.children) {
                return createColumns(dataset.children);
            } else {
                return (
                    <Column
                        key={dataset.identifier}
                        align="center"
                        field={dataset.identifier}
                        body={columnValueBodyTemplate}
                    />
                );
            }
        });
    };

    const columns = createColumns(datasetsArray);

    const footerGroup = () => {
        if (config?.showTotal && totals.length > 0) {
            return (
                <ColumnGroup>
                    <Row>
                        {!config?.hideDateColumn && <Column/>}
                        {totals.map((dataset) => {
                            return (
                                <Column
                                    key={dataset.identifier}
                                    align="center"
                                    footer={<div
                                        data-tooltip-id="footer-total-tooltip"
                                        data-tooltip-content={dataset.tooltip}
                                        data-tooltip-place="bottom"
                                    >{dataset.value}</div>
                                    }
                                />
                            );
                        })}
                    </Row>
                </ColumnGroup>
            );
        }
    };

    const dateColumnStyle = {
        minWidth: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
        maxWidth: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES]
    };

    const dateBodyTemplate = (rowData: {date: string | number | Date;}): string => {
        const dateDate = new Date(rowData.date);
        const dateFormat = config?.dateFormat || ChronoUnitLegacy.MINUTES;
        return dateToReadableString(dateDate, dateFormat as ChronoUnitLegacy);
    };

    return (
        <div>
            <Tooltip id="footer-total-tooltip" opacity={0.7} place="top" float className="z-5"/>
            <DataTable value={data} size="small" headerColumnGroup={headerGroup} footerColumnGroup={footerGroup()}>
                {!config?.hideDateColumn &&
                    <Column field="date" header="Date" align="center" body={dateBodyTemplate} style={dateColumnStyle}/>}
                {columns}
            </DataTable>
        </div>
    );
};

export default DatasetTableGrouped;
